
/**
 * IntegrationDetail — verbatim port of screens-integration-detail.jsx.
 * Full-page configuration for a single integration: required-input banner /
 * connected banner, configuration form (text · password · select · toggle ·
 * mapping fields), callback URL, optional DNS-records table, Salesforce
 * guest-sync + field-mapping panels, scopes, recent activity, help card.
 * Route: settings.integ.detail (params: { id }).
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { SegmentedControl, Btn, Card, Icon, Modal, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { ApiError, disconnectIntegration, getIntegrationDetail, getIntegrationSetupStatus, startIntegrationConnect, verifyIntegration } from "@v0y4ge/api-client";
import { ConfigField, RegistrationActivity, RegistrationPanels, RegistrationScopes, regCap, regFromResponse, regStatusKind, type RegDetail } from "./AppRegistration";
import { ProviderLogo } from "./ProviderLogo";
import { WalletPassPreview } from "./WalletPassPreview";
import { PageHeader } from "../../shell/chrome";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import SettingsShell from "./SettingsShell";
import { disconnectPayPal, getPayPalActivity, getPayPalConnection, startPayPalConnect, type PayPalConnection } from "./paypal-connect-api";
import { disconnectAdyen, getAdyenActivity, getAdyenConnectionStatus, startAdyenOnboarding, type AdyenConnection } from "./adyen-connect-api";
import { disconnectStripe, getStripeActivity, getStripeStatus, startStripeConnect, verifyStripe, type StripeActivityItem, type StripeStatusResponse } from "./stripe-connect-api";
import {
  INTEGRATION_CONFIGS, backendProviderId, integrationStatusFromDetail, refreshConnections, subscribeConnections,
  type IntegConfig, type IntegStatusKind,
} from "./integration-configs";
import { useT, t as i18nT } from "../../lib/i18n";
import {
  EthosClaimsCard, EthosConfigCard, EthosGrantChecklist, EthosResourcesCard,
  EthosTroubleshootingCard, EthosVerifyHistoryCard, ethosKind, useEthosState,
} from "./EthosIntegration";
import { EthosSyncCard, EthosSyncHistoryCard, useEthosSync } from "./EthosSyncSection";
import { DataSourceStrip } from "../../shell/opSource";
import { friendlyDateTime } from "../../lib/datetime";
import { verifyMicrosoftConnection } from "./ms-graph-api";

const thStyle: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, fontSize: 11, textTransform: "uppercase", color: T.soft, whiteSpace: "nowrap" };
const tdStyle: CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };

function BrandLogo({ id, name, brand, glyph, size = 44, radius = 10 }: { id: string; name: string; brand: string; glyph?: string; size?: number; radius?: number }) {
  return <ProviderLogo id={id} name={name} brand={brand} glyph={glyph} size={size} radius={radius} />;
}

/* Read-only connection-sourced value (REQ-045e): filled from the connection op,
   or the "Configure after connecting" placeholder — never a typed input. */
function ConnValue({ label, value, desc }: { label: string; value: string; desc?: string }) {
  const t = useT();
  return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{label}</label>
      {value ? (
        <div style={{ padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 4, background: T.white, fontSize: 13, color: T.n1, fontFamily: T.fontMono }}>{value}</div>
      ) : (
        <div style={{ padding: 12, border: `1px dashed ${T.n4}`, borderRadius: 4, background: T.white, fontSize: 13, color: T.soft }}>{t("settings.integrationDetail.configureAfterConnecting", "Configure after connecting")}</div>
      )}
      {desc ? <p style={{ fontSize: 11, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{desc}</p> : null}
    </div>
  );
}

/* ── First paint (CC 0190) ─────────────────────────────────────────────────────
 * The page renders NOTHING about a provider until the server has answered for it:
 * a skeleton while getIntegrationDetail (and, for a member, its parent
 * registration) is in flight; the Integrations list with an inline error for an
 * unknown or missing id; an error with Retry for a failed read. There is no
 * default integration: the Salesforce fallbacks are gone. Only then does the body
 * mount, already holding the server's records, so its first frame is the real one. */
type DetailLoad = { rec: RegDetail; reg: RegDetail | null; focusKey: string | null };

const isNotFoundErr = (e: unknown) => {
  const x = e as { status?: number; code?: string } | null;
  return !!x && (x.status === 404 || /(^|_)NOT_FOUND$/.test(String(x.code ?? "")));
};

async function loadIntegrationDetail(id: string, featureParam: string | null): Promise<DetailLoad | "not-found"> {
  const pid = backendProviderId(id);
  const read = async (key: string) => {
    try { return regFromResponse(await getIntegrationDetail(key)); }
    catch (e) { if (isNotFoundErr(e)) return null; throw e; }
  };
  // Alias drift: some records are keyed by the UI id verbatim (google-cal) and
  // backendProviderId maps them (google_calendar), so a miss on one tries the other.
  let rec = await read(pid);
  if (!rec && id !== pid) rec = await read(id);
  if (!rec) return "not-found";
  if (rec.sharedCredentials === true) return { rec, reg: rec, focusKey: featureParam };
  if (rec.registrationId) {
    /* A missing parent is not-found too (CC 0197): back to the list, not Retry. */
    const parent = await read(String(rec.registrationId));
    if (!parent) return "not-found";
    if (parent && parent.sharedCredentials === true) {
      return { rec, reg: parent, focusKey: featureParam ?? (rec.featureKey ? String(rec.featureKey) : null) };
    }
  }
  return { rec, reg: null, focusKey: featureParam };
}

function Bar({ w, h = 12, r = 4 }: { w: number | string; h?: number; r?: number }) {
  return <span style={{ display: "block", width: w, height: h, borderRadius: r, background: T.n6 }} />;
}

function DetailSkeleton() {
  return (
    <div role="status" aria-label="Loading integration" style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ padding: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ width: 28, height: 28, borderRadius: 999, background: T.n6, flexShrink: 0 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}><Bar w="40%" /><Bar w="70%" h={10} /></div>
        </Card>
        <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <Bar w="30%" h={14} />
          {[0, 1, 2].map((i) => <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}><Bar w="25%" h={10} /><Bar w="100%" h={34} r={4} /></div>)}
        </Card>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ width: 44, height: 44, borderRadius: 10, background: T.n6, flexShrink: 0 }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}><Bar w="60%" h={14} /><Bar w="40%" h={10} /></div>
          </div>
          <Bar w="90%" h={10} /><Bar w="75%" h={10} />
        </Card>
        <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8 }}><Bar w="45%" h={10} /><Bar w="70%" h={10} /><Bar w="60%" h={10} /></Card>
      </div>
    </div>
  );
}

export default function IntegrationDetail() {
  const t = useT();
  const nav = useNav();
  const params = (nav.params ?? {}) as Record<string, unknown>;
  const id = typeof params.id === "string" ? params.id.trim() : "";
  const featureParam = typeof params.feature === "string" ? String(params.feature) : null;
  /* Each answer is stamped with the id + feature it was loaded for (CC 0197). The router
     keeps this screen mounted across ids, so on an id change the previous answer is still
     in state for one render; only an answer for THIS key may mount the body. */
  const key = `${id}|${featureParam ?? ""}`;
  const [state, setState] = useState<{ phase: "loading"; key: string } | { phase: "error"; key: string; message: string } | { phase: "ready"; key: string; load: DetailLoad }>({ phase: "loading", key });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!id) { nav.replace("settings.integ", { notFound: "1" }); return; }
    let alive = true;
    setState({ phase: "loading", key });
    loadIntegrationDetail(id, featureParam).then(
      (r) => { if (!alive) return; if (r === "not-found") nav.replace("settings.integ", { notFound: id }); else setState({ phase: "ready", key, load: r }); },
      (e) => { if (alive) setState({ phase: "error", key, message: e instanceof Error ? e.message : String(e) }); },
    );
    return () => { alive = false; };
  }, [id, featureParam, attempt]);

  const current = state.key === key;
  if (state.phase === "ready" && current) return <IntegrationDetailBody key={key} id={id} initial={state.load} />;
  return (
    <SettingsShell activeTab="integ">
      <PageHeader
        title={<Bar w={220} h={22} r={6} />}
        breadcrumb={<>
          <span style={{ cursor: "pointer" }} onClick={() => nav.go("settings.integ")}>{t("settings.integrationDetail.integrations", "Integrations")}</span>
          <Icon.chev size={10} dir="right" />
          <Bar w={90} h={10} />
        </>}
      />
      {state.phase === "error" && current ? (
        <div style={{ padding: "16px 24px" }}>
          <Card style={{ padding: 16, display: "flex", gap: 12, alignItems: "center", background: `color-mix(in srgb, ${T.red1} 7%, ${T.white})`, borderColor: `color-mix(in srgb, ${T.red1} 25%, transparent)` }}>
            <span style={{ width: 28, height: 28, borderRadius: 999, background: T.red1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.warn size={14} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>This integration couldn't be loaded</div>
              <div style={{ fontSize: 12, color: T.n1, marginTop: 2, overflowWrap: "anywhere" }}>{state.message}</div>
            </div>
            <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} onClick={() => setAttempt((a) => a + 1)}>Retry</Btn>
          </Card>
        </div>
      ) : <DetailSkeleton />}
    </SettingsShell>
  );
}

function IntegrationDetailBody({ id, initial }: { id: string; initial: DetailLoad }) {
  const t = useT();
  const nav = useNav();
  const canWrite = can("integrations.write");
  /* The server's own record for this id (CC 0190): status and identity come from
     here, re-read after every mutation. */
  const [rec, setRec] = useState<RegDetail>(initial.rec);
  /* Static config only for an id the product genuinely has one for; otherwise a
     neutral config built from the server record. Never another provider's. */
  const hasOwnConfig = !!INTEGRATION_CONFIGS[id];
  const cfg: IntegConfig = INTEGRATION_CONFIGS[id] ?? {
    name: String(rec.name ?? id), brand: String(rec.brand ?? T.n4), icon: String(rec.icon ?? ""),
    auth: String(rec.auth ?? ""), desc: String(rec.desc ?? ""),
    inputs: [], permissions: (() => { const sc = (rec as unknown as Record<string, unknown>).scopes; return Array.isArray(sc) ? sc.map(String) : []; })(),
    docs: "", activity: [],
  };
  const [, setConnVer] = useState(0);
  useEffect(() => subscribeConnections(() => setConnVer((v) => v + 1)), []);
  const pid = backendProviderId(id);
  const st = integrationStatusFromDetail(rec);
  const reloadRec = async () => {
    try {
      let r = regFromResponse(await getIntegrationDetail(pid));
      if (!r && id !== pid) r = regFromResponse(await getIntegrationDetail(id));
      if (r) setRec(r);
    } catch (e) { console.warn(`getIntegrationDetail(${pid}) re-read failed:`, e); }
  };
  /* ── Shared-credential app registration (CD brief 2026-07-24, kit 735eab65) ──
   * Backend-driven: getIntegrationDetail(pid) says whether this provider IS a
   * shared registration (sharedCredentials: true) or a MEMBER of one
   * (registrationId + featureKey → fetch the registration, pre-focus the
   * feature). ONE reusable AppRegistration surface renders any provider. */
  /* Registration and focused feature arrive with the gate's load (CC 0190). */
  const [reg, setReg] = useState<RegDetail | null>(initial.reg);
  const [focusKey] = useState<string | null>(initial.focusKey);
  // Members present as the REGISTRATION, not the feature (operator 2026-07-22):
  // one page, one identity — e.g. "Microsoft app registration".
  const regHinted = false; // the gate resolves the registration before this mounts
  const effPid = reg ? String(reg.provider) : pid;
  /* A registration whose credential IS a file (contract `connectionModel:
   * "upload"`, e.g. Apple's Pass Type ID .p12) has NO connection lifecycle:
   * nothing to connect, re-verify or disconnect. The page therefore shows no
   * status banner and no header connection actions — the upload fields carry
   * their own state and their own replace/remove (2026-08-07, Pat). */
  /* ⭐ Chrome keyed off DATA, never a provider-name check (2026-08-07, Pratik).
   * `capabilities` states what this registration can actually do — a property of
   * the provider's auth model, not of current state. Apple's Pass Type ID
   * certificate has no connection lifecycle: verify/runNow/configure/oauthFlow/
   * apiKeyFlow all arrive false, so Verify, Disconnect and the connection status
   * banner have nothing to describe (FeatureCredentialUploads already shows the
   * real state: certificate configured / not configured). An ABSENT capability is
   * not a denial — only an explicit false is; `connectionModel: "upload"` remains
   * the equivalent signal from a producer that sends no capability set. */
  const capVerify = regCap(reg, "verify", "verify") !== false;
  const capOauthFlow = regCap(reg, "oauthFlow", "oauth_flow");
  const capApiKeyFlow = regCap(reg, "apiKeyFlow", "api_key_flow");
  const capConnLifecycle = !(capOauthFlow === false && capApiKeyFlow === false);
  /* API-key-only registration (Twilio: oauth_flow false, api_key_flow true, CD docket
   * 2026-09-15 item 1). Credentials are pasted, never granted, so there is no connect
   * flow and Verify is the ONLY liveness affordance the registration has — "requires
   * input on some other field" (an empty group-level Messaging Service SID) must not
   * remove it. Declared capabilities only: an UNSTATED oauthFlow (null) keeps the
   * legacy kind gate untouched, so no existing provider changes behaviour. */
  const apiKeyOnly = capOauthFlow === false && capApiKeyFlow !== false;
  const noConnectionLifecycle = reg?.connectionModel === "upload" || (!capVerify && !capConnLifecycle);
  const pageName = reg ? String(reg.name || cfg.name) : String(rec.name || cfg.name);
  const pageAuth = reg ? String(reg.auth ?? "") : String(rec.auth ?? cfg.auth);
  const pageDesc = reg ? String(reg.desc ?? "") : String(rec.desc ?? cfg.desc);
  // REQ-045 (merged 2026-07-23): PayPal is OAuth-onboarding only — three states
  // from the connection: not connected → "Connect with PayPal"; connected-but-
  // not-receivable → awaiting PayPal confirmation (checkout stays disabled);
  // verified/receivable → connected & verified.
  // REQ-045e: the ONLY source of PayPal connected-ness is getPayPalConnection()
  // — fetched on mount (covers the return from the onboarding redirect) and
  // POLLED while non-terminal (receivability arrives via backend webhook).
  const isPayPal = id === "paypal";
  const [ppConn, setPpConn] = useState<PayPalConnection | null>(null);
  const [ppActivity, setPpActivity] = useState<{ at: string; e: string }[]>([]);
  const [connEpoch, setConnEpoch] = useState(0); // bump → refetch + restart polling
  /* ── Payments work-order (2026-07-24) Task 1: every state explicit — loading
   * skeleton (never a blank card), redirecting, reach-error with a HUMAN message
   * mapped from the backend reason code, not-connected helper. One flag set per
   * payment provider; the banner chain below renders them in priority order. */
  const [ppLoading, setPpLoading] = useState(true);
  const [adLoading, setAdLoading] = useState(true);
  const [stLoading, setStLoading] = useState(true);
  const [payErr, setPayErr] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState<string | null>(null); // provider display name
  // Reason-code → human message map (work-order Task 1). ApiError carries the
  // backend envelope's error.code; anything unreachable falls to "couldn't reach".
  const reachErrMsg = (provider: "stripe" | "paypal" | "adyen", e: unknown): string => {
    const code = e instanceof ApiError ? e.code : "";
    if (code === "PAYMENTS_NOT_PROVISIONED" || code === "PAYPAL_PARTNER_NOT_CONFIGURED")
      return t("settings.integrationDetail.paymentsNotProvisioned", "Payments isn't fully set up yet (needs platform/partner configuration).");
    if (code === "ONBOARDING_INCOMPLETE")
      return t("settings.integrationDetail.stripeFinishOnboardingMsg", "Finish Stripe onboarding to enable charges.");
    if (e instanceof ApiError && e.message) return e.message;
    return provider === "stripe" ? t("settings.integrationDetail.stripeUnreachable", "Couldn't reach Stripe — try again.")
      : provider === "paypal" ? t("settings.integrationDetail.paypalUnreachable", "Couldn't reach PayPal — try again.")
      : t("settings.integrationDetail.adyenUnreachable", "Couldn't reach Adyen — try again.");
  };
  useEffect(() => {
    if (!isPayPal) return;
    let alive = true;
    let timer: number | undefined;
    const load = async () => {
      try {
        const c = await getPayPalConnection();
        if (!alive) return;
        setPpConn(c); setPayErr(null); setRedirecting(null);
        // Recent-activity feed (getPayPalActivity, merged #164) — degrade to [] so
        // the panel is never a doubtful empty box for an unwired op.
        getPayPalActivity().then((r) => { if (alive) setPpActivity(r?.activity ?? []); }).catch(() => { if (alive) setPpActivity([]); });
        if (c.status === "connected_unconfirmed") timer = window.setTimeout(() => { void load(); }, 3000);
      } catch (e) {
        console.warn("getPayPalConnection unavailable:", e);
        if (alive) setPayErr(reachErrMsg("paypal", e));
      } finally { if (alive) setPpLoading(false); }
    };
    void load();
    return () => { alive = false; if (timer != null) window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPayPal, connEpoch]);
  // Defect fix 2026-07-24: onboarding-callback failures were silent. The backend
  // appends ?paypal=error&reason=<code> to the return URL on a failed callback —
  // surface a mapped toast once, then strip the params (display-only; state
  // still comes ONLY from getPayPalConnection()).
  useEffect(() => {
    if (!isPayPal) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("paypal") !== "error") return;
    const reason = sp.get("reason") || "";
    const msg =
      reason === "missing_session" ? t("settings.integrationDetail.paypalErrMissingSession", "Your PayPal session expired — please start Connect again.")
      : reason === "INVALID_ONBOARDING_NONCE" ? t("settings.integrationDetail.paypalErrInvalidNonce", "That connect link expired or was already used — start again.")
      : reason === "MERCHANT_MISMATCH" ? t("settings.integrationDetail.paypalErrMerchantMismatch", "The PayPal account returned didn't match — please retry Connect.")
      : reason === "MERCHANT_ALREADY_CONNECTED" ? t("settings.integrationDetail.paypalErrMerchantAlreadyConnected", "That PayPal merchant is already connected to another workspace.")
      : reason === "MERCHANT_NOT_READY" ? t("settings.integrationDetail.paypalErrMerchantNotReady", "PayPal is still finalizing your account — this can take a minute; we'll keep checking.")
      : t("settings.integrationDetail.paypalErrGeneric", "PayPal couldn't complete the connection — please try again.");
    toast({ tone: reason === "MERCHANT_NOT_READY" ? "warn" : "error", title: t("settings.integrationDetail.paypalConnectionIssue", "PayPal connection issue"), subtitle: msg });
    sp.delete("paypal"); sp.delete("reason");
    const qs = sp.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPayPal]);
  // REQ-060: Adyen mirrors the connect flow — hosted onboarding, and the ONLY
  // source of connected-ness is getAdyenConnectionStatus(): fetched on mount
  // (covers the return from the onboarding redirect — return params are
  // display-only, never source of truth) and POLLED while "pending" (a REAL
  // intermediate state: onboarded, Adyen KYC/verification in progress, cannot
  // receive money yet — not a loading spinner).
  const isAdyen = id === "adyen";
  const [adConn, setAdConn] = useState<AdyenConnection | null>(null);
  const [adActivity, setAdActivity] = useState<{ at: string; e: string }[]>([]);
  // Stuck-verification guard (2026-07-24): "pending" lasting unusually long is a
  // real Adyen KYC outcome (more documents requested / review stuck). There is no
  // terminal-failed status in the contract yet (REQ-060g) — surface guidance
  // instead of spinning forever; Disconnect stays available to restart onboarding.
  const adPendingSinceRef = useRef<number | null>(null);
  const [adStuck, setAdStuck] = useState(false);
  useEffect(() => {
    if (!isAdyen) return;
    let alive = true;
    let timer: number | undefined;
    const load = async () => {
      try {
        const c = await getAdyenConnectionStatus();
        if (!alive) return;
        setAdConn(c);
        setPayErr(null); setRedirecting(null);
        // Recent-activity feed (getAdyenActivity, merged #164) — degrade to [].
        getAdyenActivity().then((r) => { if (alive) setAdActivity(r?.activity ?? []); }).catch(() => { if (alive) setAdActivity([]); });
        if (c.status === "pending") {
          if (adPendingSinceRef.current == null) adPendingSinceRef.current = Date.now();
          setAdStuck(Date.now() - adPendingSinceRef.current > 60000);
          timer = window.setTimeout(() => { void load(); }, 3000);
        } else {
          adPendingSinceRef.current = null;
          setAdStuck(false);
        }
      } catch (e) {
        console.warn("getAdyenConnectionStatus unavailable:", e);
        if (alive) setPayErr(reachErrMsg("adyen", e));
      } finally { if (alive) setAdLoading(false); }
    };
    void load();
    return () => { alive = false; if (timer != null) window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdyen, connEpoch]);
  // Stripe FE handoff (uploads/stripe-fe-handoff 2026-07-23, REQ-064): Stripe
  // reflects the REAL backend connection — GET /status on mount (and after the
  // OAuth redirect returns ?stripe=connected), GET /activity for the feed.
  // Degrades gracefully (docket design-time §): status → null, activity → [],
  // so the screen renders the clean not-connected state and never crashes.
  const isStripe = id === "stripe";
  const [stConn, setStConn] = useState<StripeStatusResponse | null>(null);
  const [stActivity, setStActivity] = useState<StripeActivityItem[]>([]);
  const refreshStripe = () => {
    // Work-order Task 1: a THROWN status fetch (network / 5xx) is a visible
    // reach-error state — only a 404 (→ null inside the api) is a clean
    // not-connected. Activity stays degrade-to-empty by contract.
    getStripeStatus()
      .then((s) => { setStConn(s); setPayErr(null); setRedirecting(null); })
      .catch((e) => { setStConn(null); setPayErr(reachErrMsg("stripe", e)); })
      .finally(() => setStLoading(false));
    getStripeActivity().then(setStActivity).catch(() => setStActivity([]));
  };
  useEffect(() => {
    if (!isStripe) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("stripe") === "connected") {
      toast({ tone: "success", title: t("settings.integrationDetail.stripeConnectedToast", "Stripe connected") });
      sp.delete("stripe"); // one-shot flag — drop it so a reload doesn't re-toast
      const qs = sp.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    }
    refreshStripe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStripe, connEpoch]);
  const stAccount = stConn?.connection?.account_id ?? null;
  // Connected but not yet onboarded (charges_enabled=false server-side).
  const stNeedsAttention = !!stAccount && stConn?.status === "needs-attention";
  // Handoff banner detail: real acct id (mono) + test/live mode. The
  // "· verified <ts>" interpolation stays literal until its ICU key ships
  // (settings.integrationDetail.stripeVerifiedAt.icu — REQ-064).
  const stConnDetail = (
    <>{t("settings.integrationDetail.stripeAccountWord", "Account")} <code style={{ fontFamily: T.fontMono }}>{stAccount || "—"}</code>{stConn?.connection?.livemode ? "" : t("settings.integrationDetail.stripeTestMode", " · test mode")}</>
  );
  // Recent activity (docket §2): every payment provider now renders its REAL
  // backend feed (Stripe: connection+verification events with success/failure dot;
  // PayPal/Adyen: connection-lifecycle events, merged #164). Non-payment providers
  // keep the server-fed cfg.activity rows. Timestamps via lib/datetime (house std).
  type ActivityRow = { e: string; at: string; outcome?: "success" | "failure" };
  const activityRows: ActivityRow[] = isStripe
    ? stActivity.map((a) => ({ e: a.detail, at: friendlyDateTime(a.occurred_at), outcome: a.outcome }))
    : isPayPal
    ? ppActivity.map((a) => ({ e: a.e, at: friendlyDateTime(a.at) }))
    : isAdyen
    ? adActivity.map((a) => ({ e: a.e, at: friendlyDateTime(a.at) }))
    : (cfg.activity as ActivityRow[]);
  /* ── Ellucian Ethos (CD brief 2026-08-19-0002) ────────────────────────────
   * An API-key provider whose Verify is an onboarding PREFLIGHT, not a ping: it
   * authenticates, then diffs the resources the college granted the application
   * against the ones this lane reads. That diff is a checklist, so Ethos owns its
   * own config + checklist panels (EthosIntegration.tsx) and its own status
   * derivation — and has no Connect button at all, because there is no OAuth step
   * and no connected account to authorize. */
  const isEthos = id === "ethos";
  const ethos = useEthosState(isEthos);
  /* The recurring customer sync is a SECOND surface on the same connection —
   * its own operations, its own version check, its own run history. It reads
   * nothing until this is the Ethos page. */
  const ethosSync = useEthosSync(isEthos);
  const isPayments = isPayPal || isAdyen || isStripe;
  const payLoading = isPayPal ? ppLoading : isAdyen ? adLoading : isStripe ? stLoading : false;
  // Work-order Task 1 "what happens" helper for the explicit not-connected state.
  const whatHappens = isStripe
    ? t("settings.integrationDetail.stripeWhatHappens", "Connect opens Stripe to authorize your account — keys, webhooks and currency are provisioned automatically; there's nothing to paste.")
    : isPayPal
      ? t("settings.integrationDetail.paypalWhatHappens", "Connect opens PayPal onboarding — credentials and webhooks are provisioned automatically. Checkout enables once PayPal confirms your account can receive payments.")
      : t("settings.integrationDetail.adyenWhatHappens", "Connect opens Adyen's hosted onboarding — no API keys to paste. Checkout enables once Adyen finishes verifying the account (KYC).");
  const kind: IntegStatusKind = isEthos
    ? (() => {
        const k = ethosKind(ethos);
        return k === "connected" ? "connected" : k === "configured-unverified" ? "configured-unverified" : "disconnected";
      })()
    : isPayPal
    ? (!ppConn || ppConn.status === "not_connected" ? "disconnected" : ppConn.status === "verified" ? "connected" : "configured-unverified")
    : isAdyen
      ? (!adConn || adConn.status === "not_connected" ? "disconnected" : adConn.status === "verified" ? "connected" : "configured-unverified")
      : isStripe
        ? (!stAccount ? "disconnected" : stNeedsAttention ? "configured-unverified" : "connected")
        : reg ? regStatusKind(reg) : st.kind;
  // PayPal state copy — the merchant/currency interpolations stay literal until
  // their ICU keys ship (i18n hard rule: never an ICU call before the key
  // exists). Suggested keys: settings.integrationDetail.paypalUnverifiedSub.icu
  // / paypalConnectedSub.icu — filed as REQ-058.
  const unverifiedTitle = isPayPal ? t("settings.integrationDetail.paypalAwaitingConfirmation", "Connected — awaiting PayPal confirmation") : isAdyen ? t("settings.integrationDetail.adyenVerificationPending", "Connected — verification pending") : isStripe ? t("settings.integrationDetail.stripeNeedsAttentionTitle", "Connected — needs attention") : t("settings.integrationDetail.configuredNotVerified", "Configured — not verified");
  const unverifiedSub = isEthos
    ? t("settings.ethos.unverifiedSub", "Credentials are stored, but the resource grants haven't been checked. Verify authenticates the key and names any grant this lane is missing.")
    : isPayPal
    ? `Merchant ${ppConn?.merchantId || "—"} · PayPal hasn't confirmed this account can receive payments yet — PayPal checkout stays disabled until it does.`
    : isAdyen
      ? <>{t("settings.integrationDetail.adyenKycInProgress", "Adyen is verifying this account (KYC in progress) — the account can't receive money yet, so Adyen checkout stays disabled until verification completes.")}{" "}{adStuck ? <> {t("settings.integrationDetail.adyenVerificationTakingLong", "This is taking longer than usual — you can keep waiting (we re-check automatically), or disconnect and restart onboarding if Adyen asked for more documents.")}</> : null}</>
      : isStripe
        ? <>{stConnDetail}{t("settings.integrationDetail.stripeFinishOnboarding", " · finish Stripe onboarding to enable charges")}</>
        : t("settings.integrationDetail.credentialsAreStoredButNoLiveCheckHasPassedYet", "Credentials are stored but no live check has passed yet.");
  const connectedTitle = isPayPal ? t("settings.integrationDetail.paypalConnectedVerified", "Connected & verified") : isAdyen ? t("settings.integrationDetail.adyenConnectedVerified", "Connected & verified") : t("settings.integrationDetail.connectedAndHealthy", "Connected and healthy");
  const connectedSub = isEthos
    ? (ethos.verify?.accountLabel || t("settings.ethos.connectedSub", "Every required resource grant is in place."))
    : isPayPal
    ? `Merchant ${ppConn?.merchantId || "—"} · ${ppConn?.currency || "—"} · this account can receive payments — PayPal is live at checkout.`
    : isAdyen
      // Mode interpolation stays literal until its ICU key ships (REQ-060e).
      ? `${adConn?.mode === "live" ? "Live" : "Test"} mode · this account can receive payments — Adyen is live at checkout.`
      : isStripe
        // "verified <ts>" interpolation literal pending its ICU key (REQ-064).
        ? <>{stConnDetail}{stConn?.connection?.last_verified_at ? <> {t("settings.integrationDetail.text", "· verified")}{" "}{friendlyDateTime(stConn.connection.last_verified_at)}</> : ""}</>
        : t("settings.integrationDetail.verifiedAgainstTheProvider", "Verified against the provider");

  // §6/§7 (kit e670c958): connect / disconnect / verify are the REAL integration
  // ops — success feedback only after the awaited call resolves; failures render
  // as error toasts, never as success.
  const [busyAct, setBusyAct] = useState<"connect" | "disconnect" | "verify" | null>(null);
  // Item 2 (addendum): shared-registration Disconnect goes through a confirmation
  // modal that names the cascade effects, not window.confirm.
  const [confirmDisc, setConfirmDisc] = useState(false);
  const opErr = (title: string, e: unknown) => toast({ tone: "warn", title, subtitle: e instanceof Error ? e.message : String(e) });
  const doConnect = async () => {
    // API-key registrations are configured by their field PATCHes; they have no OAuth start route.
    if (capOauthFlow === false || busyAct) return; setBusyAct("connect");
    try {
      if (isPayPal) {
        // REQ-045e: the backend mints the onboarding URL — never built or
        // hardcoded FE-side. Navigate to it; PayPal returns the browser to the
        // app's callback route and the mount fetch + poll pick up the status.
        const r = await startPayPalConnect();
        if (!r || typeof r.redirectUrl !== "string" || !r.redirectUrl) throw new Error("connect/start returned no redirect URL");
        setRedirecting("PayPal");
        window.location.assign(r.redirectUrl);
        // Real backend: the page unloads to PayPal here and the mount fetch runs
        // on return. Preview mock: the redirectUrl is a same-page hash change —
        // restart the connection fetch/poll so the new status renders live.
        setConnEpoch((e) => e + 1);
        return;
      }
      if (isAdyen) {
        // REQ-060d: the backend builds the single-use, short-lived hosted-
        // onboarding link — request a FRESH one on every click (never cached)
        // and full-page redirect to it. On return the mount fetch + poll re-read
        // getAdyenConnectionStatus — return params are display-only.
        const r = await startAdyenOnboarding({ returnUrl: window.location.href });
        if (!r || typeof r.onboardingUrl !== "string" || !r.onboardingUrl) throw new Error("onboarding/start returned no onboarding URL");
        setRedirecting("Adyen");
        window.location.assign(r.onboardingUrl);
        // Real backend: the page unloads to Adyen here. Preview mock: the
        // onboardingUrl is a same-page hash change — restart the fetch/poll.
        setConnEpoch((e) => e + 1);
        return;
      }
      if (isStripe) {
        // REQ-064 (fix 2026-07-24): uniform with PayPal/Adyen — the backend mints
        // the authorize URL and returns it as JSON { url }; navigate to it. Real:
        // Stripe's consent screen (?stripe=connected return + mount fetch pick up
        // the new status). Preview mock: url is the integration-detail route with
        // the connection persisted across the reload — bump connEpoch so the
        // status/activity fetch re-runs live. Was a 302 + __CD_PREVIEW_STRIPE_MOCK__
        // flag that left the Claude Design preview stuck.
        const r = await startStripeConnect(window.location.href);
        if (!r || typeof r.url !== "string" || !r.url) throw new Error("connect/start returned no consent URL");
        setRedirecting("Stripe");
        window.location.assign(r.url);
        setConnEpoch((e) => e + 1);
        return;
      }
      const res = await startIntegrationConnect(effPid, { redirect_to: window.location.href }) as { url?: string };
      if (!res || typeof res.url !== "string" || !res.url) throw new Error("connect/start returned no consent URL");
      window.open(res.url, "_blank", "noopener");
      toast({ tone: "success", title: i18nT("settings.integrationDetail.continueInCfgName.icu", "Continue in {value0}", { value0: pageName }), subtitle: t("settings.integrationDetail.finishAuthorizingInTheProviderWindowThenVerifyTheConnectionHere", "Finish authorizing in the provider window, then verify the connection here.") });
      await refreshConnections(); await reloadRec();
    } catch (e) {
      // Work-order Task 1: connect failures surface the MAPPED reason (e.g. the
      // broker's 503 PAYMENTS_NOT_PROVISIONED / PAYPAL_PARTNER_NOT_CONFIGURED),
      // never a bare failure; a started redirect state is cleared.
      setRedirecting(null);
      if (isPayments) toast({ tone: "error", title: t("settings.integrationDetail.couldNotStartConnectionFor", "Couldn't start the connection for {page}", { page: pageName }), subtitle: reachErrMsg(isStripe ? "stripe" : isPayPal ? "paypal" : "adyen", e) });
      else opErr(`${t("settings.integrationDetail.couldnTStartConnect", "Couldn't start the connection for")} ${pageName}`, e);
    } finally { setBusyAct(null); }
  };
  const doDisconnect = async () => {
    if (busyAct) return;
    // Work-order Task 1: Disconnect → confirm, then success toast. Native
    // confirm() is the house interim (AccountDetails precedent, REQ-048(F)).
    if (isPayments && !window.confirm(t("settings.integrationDetail.disconnectPaymentConfirm", "Disconnect this payment account? Guests won't be able to check out with it until you reconnect."))) return;
    setBusyAct("disconnect");
    try {
      if (isPayPal) {
        await disconnectPayPal();
        const c = await getPayPalConnection(); // re-read the single source of truth
        setPpConn(c);
        toast({ tone: "success", title: t("settings.integrationDetail.paypalDisconnected", "PayPal disconnected") });
        return;
      }
      if (isAdyen) {
        await disconnectAdyen();
        const c = await getAdyenConnectionStatus(); // re-read the single source of truth
        setAdConn(c);
        toast({ tone: "success", title: t("settings.integrationDetail.adyenDisconnected", "Adyen disconnected") });
        return;
      }
      if (isStripe) {
        // Docket §4: the OAuth-namespaced route (connect/disconnect) — NOT the
        // generic /{provider}/disconnect used by API-key providers.
        await disconnectStripe();
        refreshStripe(); // re-read the single source of truth
        toast({ tone: "warn", title: t("settings.integrationDetail.stripeDisconnected", "Stripe disconnected") });
        return;
      }
      await disconnectIntegration(effPid);
      await refreshConnections(); await reloadRec();
      if (isEthos) await ethos.reload();
      // Item 2: re-fetch the registration and re-render from the cascaded record
      // (creds cleared, every feature off, scopes empty, requires-input).
      if (reg) { const rec = regFromResponse(await getIntegrationDetail(effPid)); if (rec) setReg(rec); }
      toast({ tone: "success", title: i18nT("settings.integrationDetail.cfgNameDisconnected.icu", "{value0} disconnected", { value0: pageName }) });
    } catch (e) { opErr(`Couldn't disconnect ${pageName}`, e); } finally { setBusyAct(null); }
  };
  const doVerify = async () => {
    if (busyAct) return; setBusyAct("verify");
    try {
      if (isEthos) {
        // Ethos verify writes into the checklist panel, so it runs through the
        // Ethos state — a bare toast would throw away the per-resource result the
        // whole preflight exists to produce.
        await ethos.runVerify();
        return;
      }
      if (isPayPal) {
        // "Verify connection" for PayPal = re-fetch the connection status.
        const c = await getPayPalConnection();
        setPpConn(c);
        if (c.status === "verified") toast({ tone: "success", title: t("settings.integrationDetail.paypalConnectedVerified", "Connected & verified"), subtitle: t("settings.integrationDetail.paypalReceivableConfirmed", "PayPal confirmed this account can receive payments.") });
        else if (c.status === "connected_unconfirmed") toast({ tone: "warn", title: t("settings.integrationDetail.paypalStillAwaitingConfirmation", "Still awaiting PayPal confirmation"), subtitle: t("settings.integrationDetail.paypalConfirmsReceivabilityViaWebhook", "PayPal confirms receivability via webhook shortly after onboarding — checking again automatically.") });
        else toast({ tone: "warn", title: t("settings.integrations.notConnected", "Not connected"), subtitle: t("settings.integrationDetail.startWithConnectWithPaypal", "Start with Connect with PayPal.") });
        return;
      }
      if (isAdyen) {
        // "Test connection" for Adyen = re-fetch the connection status.
        const c = await getAdyenConnectionStatus();
        setAdConn(c);
        if (c.status === "verified") toast({ tone: "success", title: t("settings.integrationDetail.adyenConnectedVerified", "Connected & verified"), subtitle: t("settings.integrationDetail.adyenReceivableConfirmed", "Adyen confirmed this account can receive payments.") });
        else if (c.status === "pending") toast({ tone: "warn", title: t("settings.integrationDetail.adyenStillVerifying", "Verification still pending"), subtitle: t("settings.integrationDetail.adyenVerifiesAfterOnboarding", "Adyen completes KYC/verification after onboarding — checking again automatically.") });
        else toast({ tone: "warn", title: t("settings.integrations.notConnected", "Not connected"), subtitle: t("settings.integrationDetail.startWithConnectWithAdyen", "Start with Connect with Adyen.") });
        return;
      }
      if (isStripe) {
        // Docket §4: Test connection = the real POST /verify via the central
        // broker (was a hardcoded "healthy" toast pre-handoff).
        toast({ tone: "info", title: t("settings.integrationDetail.stripeTesting", "Testing Stripe connection…") });
        const r0 = await verifyStripe();
        if (r0.ok) toast({ tone: "success", title: t("settings.integrationDetail.stripeConnectionHealthy", "Stripe connection healthy") });
        // Work-order Task 1: verify's ONBOARDING_INCOMPLETE maps to the
        // needs-attention message, not a generic failure.
        else if (r0.error?.code === "ONBOARDING_INCOMPLETE") toast({ tone: "warn", title: t("settings.integrationDetail.stripeNeedsAttentionTitle", "Connected — needs attention"), subtitle: t("settings.integrationDetail.stripeFinishOnboardingMsg", "Finish Stripe onboarding to enable charges.") });
        else toast({ tone: "warn", title: t("settings.integrationDetail.stripeError", "Stripe: {message}", { message: r0.error?.message ?? t("settings.integrationDetail.stripeVerificationFailed", "verification failed") }) });
        refreshStripe(); // refresh status + activity with the new verification
        return;
      }
      if (reg && reg.provider === "microsoft") {
        /* The page's ONE Verify (CC 0135 §2), in the status banner: Microsoft's own
           check, its result read from the payload, then the record re-read. */
        const raw = await verifyMicrosoftConnection() as { data?: { ok?: boolean; latency_ms?: number; error?: { message?: string } | null } } & { ok?: boolean; error?: { message?: string } | null };
        const v = (raw && raw.data) ? raw.data : raw;
        await refreshConnections(); await reloadRec();
        if (v?.ok) toast({ tone: "success", title: t("settings.integrationDetail.connectionVerified", "Connection verified") });
        else toast({ tone: "warn", title: t("settings.integrationDetail.verificationFailed", "Verification failed"), subtitle: v?.error?.message || "Microsoft refused the saved credentials." });
        return;
      }
      const r = await verifyIntegration(effPid);
      await refreshConnections(); await reloadRec();
      if (r.data.ok) toast({ tone: "success", title: t("settings.integrationDetail.connectionVerified", "Connection verified"), subtitle: r.data.latency_ms != null ? `${pageName} responded in ${r.data.latency_ms} ms.` : undefined });
      else toast({ tone: "warn", title: t("settings.integrationDetail.verificationFailed", "Verification failed"), subtitle: r.data.error?.message || "The provider rejected the stored credentials." });
    } catch (e) { opErr(`Couldn't verify ${pageName}`, e); } finally { setBusyAct(null); }
  };

  // Docket §4: manual banner Refresh — unlike the silent on-load fetch, this
  // confirms the result with a per-status toast. (Handoff tone "danger" →
  // house ToastType "error".)
  const handleStripeRefresh = () => {
    toast({ tone: "info", title: t("settings.integrationDetail.stripeRefreshing", "Refreshing Stripe status…") });
    Promise.all([getStripeStatus(), getStripeActivity()])
      .then(([s, acts]) => {
        setStConn(s); setStActivity(acts);
        if (s?.status === "connected") toast({ tone: "success", title: t("settings.integrationDetail.stripeConnectionHealthy", "Stripe connection healthy") });
        else if (s?.status === "needs-attention") toast({ tone: "warn", title: t("settings.integrationDetail.stripeNeedsAttentionToast", "Stripe — needs attention"), subtitle: s?.connection?.last_error ?? undefined });
        else if (!s || !s.connection) toast({ tone: "warn", title: t("settings.integrationDetail.stripeNotConnectedToast", "Stripe is not connected") });
        else toast({ tone: "success", title: t("settings.integrationDetail.stripeStatusRefreshed", "Stripe status refreshed") });
      })
      .catch(() => toast({ tone: "error", title: t("settings.integrationDetail.stripeRefreshFailed", "Could not refresh Stripe status") }));
  };

  // §7: gmeet + msteams expose a server-checked setup walkthrough status
  // (zoom-style shape) — loaded read-only, degrades silently if unavailable.
  type SetupStatus = Awaited<ReturnType<typeof getIntegrationSetupStatus>>;
  const [setup, setSetup] = useState<SetupStatus | null>(null);
  const hasSetupStatus = pid === "gmeet" || pid === "msteams" || pid === "zoom";
  useEffect(() => {
    let alive = true;
    setSetup(null);
    if (!hasSetupStatus) return;
    getIntegrationSetupStatus(pid)
      .then((s) => { if (alive) setSetup(s); })
      .catch((e) => console.warn(`setup-status(${pid}) unavailable:`, e));
    return () => { alive = false; };
  }, [pid, hasSetupStatus]);

  return (
    <SettingsShell activeTab="integ">
      <PageHeader
        title={pageName}
        subtitle={i18nT("settings.integrationDetail.cfgAuthCfgDesc.icu", "{value0} · {value1}", { value0: pageAuth, value1: pageDesc })}
        breadcrumb={<>
          <span style={{ cursor: "pointer" }} onClick={() => nav.go("dashboard")}>{t("settings.integrationDetail.dashboard", "Dashboard")}</span>
          <Icon.chev size={10} dir="right" />
          <span style={{ cursor: "pointer" }} onClick={() => nav.go("settings.integ")}>{t("settings.integrationDetail.integrations", "Integrations")}</span>
          <Icon.chev size={10} dir="right" />
          <span style={{ color: T.n1 }}>{pageName}</span>
        </>}
        actions={payLoading || noConnectionLifecycle ? null : <>
          {/* Microsoft keeps one Verify, in the status banner (CC 0135 §2). */}
          {(kind !== "disconnected" || isEthos || apiKeyOnly) && !(reg && reg.provider === "microsoft") ? (capVerify ? <Btn variant="secondary" icon={<Icon.refresh size={14} />} disabled={busyAct != null || ethos.verifying} onClick={() => { void doVerify(); }}>{busyAct === "verify" || ethos.verifying ? t("settings.integrationDetail.verifying", "Verifying…") : isStripe ? t("settings.integrationDetail.testConnection", "Test connection") : t("settings.integrationDetail.verifyConnection", "Verify connection")}</Btn> : null) : null}
          {!capConnLifecycle || (isEthos && kind === "disconnected") ? null : kind === "connected" || kind === "configured-unverified"
            ? (canWrite ? <Btn variant="secondary" danger icon={<Icon.logout size={14} />} disabled={busyAct != null} onClick={() => { if (reg) { setConfirmDisc(true); } else { void doDisconnect(); } }}>{busyAct === "disconnect" ? t("settings.integrationDetail.disconnecting", "Disconnecting…") : t("account.details.disconnect", "Disconnect")}</Btn> : null)
            : (capOauthFlow !== false && canWrite && !(reg && reg.provider === "microsoft") ? <Btn icon={<Icon.network size={14} />} disabled={busyAct != null} onClick={() => { void doConnect(); }}>{busyAct === "connect" ? t("settings.integrationDetail.opening", "Opening…") : isPayPal ? t("settings.integrationDetail.connectWithPaypal", "Connect with PayPal") : isAdyen ? t("settings.integrationDetail.connectWithAdyen", "Connect with Adyen") : t("settings.integrationDetail.connectCfgName", "Connect {value0}", { value0: pageName })}</Btn> : null)}
        </>}
      />
      {isStripe ? <DataSourceStrip screen="settings-integration-stripe" ops={["getStripeStatus", "getStripeActivity", "verifyStripe", "startStripeConnect", "disconnectStripe"]} reqId="REQ-064" /> : null}
      {isAdyen ? <DataSourceStrip screen="settings-integration-adyen" ops={["startAdyenOnboarding", "getAdyenConnectionStatus", "getAdyenActivity", "disconnectAdyen"]} reqId="REQ-060" /> : null}

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Status banner — §6 honest states (kit e670c958) + payments
           * work-order 2026-07-24 Task 1 explicit-state chain: loading skeleton →
           * reach-error (mapped reason + retry) → redirecting → not-connected
           * helper → the existing needs-attention / connected / error banners. */}
          {noConnectionLifecycle ? null : isPayments && payLoading ? (
            <Card style={{ padding: 14, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ width: 28, height: 28, borderRadius: 999, background: T.n6, flexShrink: 0 }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ width: "40%", height: 12, borderRadius: 4, background: T.n6 }} />
                <span style={{ width: "70%", height: 10, borderRadius: 4, background: T.n6 }} />
              </div>
              <span style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.checkingConnection", "Checking connection…")}</span>
            </Card>
          ) : isPayments && payErr ? (
            <Card style={{ padding: 14, background: `color-mix(in srgb, ${T.red1} 7%, ${T.white})`, borderColor: `color-mix(in srgb, ${T.red1} 25%, transparent)`, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ width: 28, height: 28, borderRadius: 999, background: T.red1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon.warn size={14} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.red1 }}>{t("settings.integrationDetail.connectionCheckFailed", "Couldn't check the connection")}</div>
                <div style={{ fontSize: 12, color: T.n1 }}>{payErr}</div>
              </div>
              <Btn variant="ghost" size="sm" icon={<Icon.refresh size={12} />} onClick={() => { setPayErr(null); if (isPayPal) setPpLoading(true); if (isAdyen) setAdLoading(true); if (isStripe) setStLoading(true); setConnEpoch((e) => e + 1); }}>{t("settings.integrationDetail.retry", "Retry")}</Btn>
            </Card>
          ) : isPayments && redirecting ? (
            <Card style={{ padding: 14, background: T.blue2, borderColor: `color-mix(in srgb, ${T.blue1} 19%, transparent)`, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ width: 28, height: 28, borderRadius: 999, background: T.blue1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon.network size={14} />
              </span>
              <div style={{ flex: 1 }}>
                {/* Provider-name interpolation stays literal pending its ICU key (REQ-065). */}
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.integrationDetail.text2", "Redirecting to")}{" "}{redirecting}…</div>
                <div style={{ fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.redirectingSub", "Finish authorizing in the provider window — this page updates when you return.")}</div>
              </div>
            </Card>
          ) : isPayments && kind === "disconnected" ? (
            <Card style={{ padding: 14, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ width: 28, height: 28, borderRadius: 999, background: T.n6, color: T.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon.network size={14} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.integrations.notConnected", "Not connected")}</div>
                <div style={{ fontSize: 12, color: T.soft }}>{whatHappens}</div>
              </div>
            </Card>
          ) : kind === "error" ? (
            <Card style={{ padding: 16, background: `color-mix(in srgb, ${T.red1} 7%, ${T.white})`, borderColor: `color-mix(in srgb, ${T.red1} 25%, transparent)` }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ width: 32, height: 32, borderRadius: 8, background: T.red1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon.warn size={16} />
                </span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.red1 }}>{reg ? (reg.lastError?.code === "INTEGRATION_CREDENTIAL_EXPIRED" ? "The client secret has expired" : "The provider refused these credentials") : t("settings.integrationDetail.storedCredentialsCanTBeRead", "Stored credentials can't be read")}</div>
                  {/* A registration shows the backend's own lastError (CC 0118), e.g. an expired client secret. */}
                  <div style={{ fontSize: 13, color: T.n1, marginTop: 4 }}>{reg ? (reg.lastError?.message || "The last check against the provider failed.") : (st.lastError || t("settings.integrationDetail.text4", "The saved secrets are undecryptable (key rotation)."))}</div>
                  {reg && reg.provider === "microsoft" ? <div style={{ marginTop: 8 }}><Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} disabled={busyAct != null} onClick={() => { void doVerify(); }}>{busyAct === "verify" ? t("settings.integrationDetail.verifying", "Verifying…") : t("settings.integrationDetail.verify", "Verify")}</Btn></div> : null}
                  <div style={{ fontSize: 12, color: T.n2, marginTop: 8 }}>{t("settings.integrationDetail.reEnterEverySecretFieldThen", "Re-enter every secret field, then")}{" "}<strong>{t("settings.integrationDetail.connect", "Connect")}{" "}{cfg.name}</strong> {t("settings.integrationDetail.againSavingOtherSettingsInThisStateIsRejectedProviderSecretsUnreadable", "again. Saving other settings in this state is rejected (PROVIDER_SECRETS_UNREADABLE).")}</div>
                </div>
              </div>
            </Card>
          ) : kind === "configured-unverified" ? (
            <Card style={{ padding: 14, background: T.orange2, borderColor: `color-mix(in srgb, ${T.orange1} 19%, transparent)`, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ width: 28, height: 28, borderRadius: 999, background: T.orange1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Icon.warn size={14} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{unverifiedTitle}</div>
                <div style={{ fontSize: 12, color: T.soft }}>{unverifiedSub}</div>
              </div>
              {isStripe
                ? <Btn variant="ghost" size="sm" icon={<Icon.refresh size={12} />} onClick={handleStripeRefresh}>{t("settings.integrationDetail.stripeRefresh", "Refresh")}</Btn>
                : <Btn variant="ghost" size="sm" icon={<Icon.refresh size={12} />} disabled={busyAct != null} onClick={() => { void doVerify(); }}>{t("settings.integrationDetail.verify", "Verify")}</Btn>}
            </Card>
          ) : kind === "connected" ? (
            <Card style={{ padding: 14, background: T.green2, borderColor: `color-mix(in srgb, ${T.green1} 19%, transparent)`, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ width: 28, height: 28, borderRadius: 999, background: T.green1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Icon.check size={14} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{connectedTitle}</div>
                <div style={{ fontSize: 12, color: T.soft }}>{connectedSub}</div>
              </div>
              {isStripe
                ? <Btn variant="ghost" size="sm" icon={<Icon.refresh size={12} />} onClick={handleStripeRefresh}>{t("settings.integrationDetail.stripeRefresh", "Refresh")}</Btn>
                : <Btn variant="ghost" size="sm" icon={<Icon.refresh size={12} />} disabled={busyAct != null} onClick={() => { void doVerify(); }}>{t("settings.integrationDetail.reVerify", "Re-verify")}</Btn>}
            </Card>
          ) : cfg.needsInput && !reg && !regHinted ? (
            <Card style={{ padding: 16, background: T.orange2, borderColor: `color-mix(in srgb, ${T.orange1} 25%, transparent)` }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ width: 32, height: 32, borderRadius: 8, background: T.orange1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon.warn size={16} />
                </span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.orange1 }}>{t("settings.integrationDetail.requiresAdditionalInputToConnect", "Requires additional input to connect")}</div>
                  <div style={{ fontSize: 13, color: T.n1, marginTop: 4 }}>{cfg.missing}</div>
                  <div style={{ fontSize: 12, color: T.n2, marginTop: 8 }}>{t("settings.integrationDetail.fillInTheFieldsBelowThenClick", "Fill in the fields below, then click")}{" "}<strong>{t("settings.integrationDetail.connect", "Connect")}{" "}{cfg.name}</strong>.</div>
                </div>
              </div>
            </Card>
          ) : null}
          {/* Microsoft admin consent now sits under the credentials, inside RegistrationPanels (CC 0156). */}

          {/* Configuration — Ethos renders its own credential form + the grant
           * checklist (both driven by the served record and the verify result);
           * shared app registrations render the backend-driven AppRegistration
           * surface (kit 735eab65); standalone integrations keep the
           * per-integration form. */}
          {isEthos ? (
            <>
              <EthosConfigCard state={ethos} canWrite={canWrite} />
              <EthosGrantChecklist state={ethos} canWrite={canWrite} />
              <EthosSyncCard state={ethosSync} canWrite={canWrite} />
              <EthosSyncHistoryCard state={ethosSync} />
              <EthosTroubleshootingCard state={ethos} />
            </>
          ) : reg ? (
            <RegistrationPanels detail={reg} focusKey={focusKey} canWrite={canWrite} onDetail={setReg} />
          ) : regHinted ? (
            <Card style={{ padding: 24, fontSize: 12, color: T.soft }}>{t("settings.integrationDetail.loadingRegistration", "Loading registration…")}</Card>
          ) : (
          <Card style={{ padding: 0 }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.configuration", "Configuration")}</h3>
              <p style={{ ...tx.help, marginTop: 2 }}>{cfg.docs}</p>
            </div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
              {isPayPal ? (
                // REQ-045e: merchant + currency render ONLY from getPayPalConnection().
                <>
                  <ConnValue label={t("settings.integrationDetail.paypalMerchant", "PayPal merchant")} value={kind === "disconnected" ? "" : (ppConn?.merchantId || "")} desc={t("settings.integrationDetail.paypalMerchantDesc", "Provisioned by PayPal during onboarding — read-only.")} />
                  <ConnValue label={t("settings.integrationDetail.defaultCurrency", "Default currency")} value={(kind === "disconnected" ? "" : ppConn?.currency) || ""} desc={t("settings.integrationDetail.paypalCurrencyDesc", "Set during PayPal onboarding from your merchant profile — read-only.")} />
                </>
              ) : isAdyen ? (
                // REQ-060: no per-tenant credentials — API key / merchant account /
                // HMAC are platform secrets held by the backend and NEVER entered
                // here. Environment renders ONLY from getAdyenConnectionStatus().
                <ConnValue label={t("settings.integrationDetail.environment", "Environment")} value={kind === "disconnected" ? "" : adConn?.mode === "live" ? "Live" : adConn?.mode === "test" ? "Test (sandbox)" : ""} desc={t("settings.integrationDetail.adyenEnvironmentDesc", "Set by the platform — test mode until live credentials are configured. Read-only.")} />
              ) : isStripe ? (
                // Docket §3: Stripe account + Default currency are provisioned by
                // OAuth — display-only, "nothing to paste" (currency shows the
                // design default until /status returns default_currency — BE
                // follow-up in the docket). Fee passthrough stays editable.
                <>
                  <ConnValue label={t("settings.integrationDetail.stripeAccountLabel", "Stripe account")} value={stAccount || ""} desc={t("settings.integrationDetail.stripeAccountDesc", "Provisioned automatically when you connect — read-only.")} />
                  <ConnValue label={t("settings.integrationDetail.defaultCurrency", "Default currency")} value={stAccount ? (cfg.inputs[1]?.connectedFilled || "USD") : ""} desc={t("settings.integrationDetail.stripeCurrencyDesc", "Read from your connected Stripe account.")} />
                  {cfg.inputs.filter((f) => f.type === "toggle").map((f, i) => <ConfigField key={i} f={f} canWrite={canWrite} />)}
                </>
              ) : cfg.inputs.map((f, i) => <ConfigField key={i} f={f} canWrite={canWrite} />)}
              {cfg.callback ? (
                <div style={{ padding: 12, background: T.n6, borderRadius: 6, border: `1px solid ${T.border}` }}>
                  <div style={{ ...tx.label, marginBottom: 6 }}>{t("settings.integrationDetail.callbackWebhookUrl", "Callback / webhook URL")}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <code style={{ flex: 1, fontFamily: T.fontMono, fontSize: 12, color: T.p1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cfg.callback}</code>
                    <Btn variant="ghost" size="sm" icon={<Icon.copy size={12} />} onClick={() => { try { navigator.clipboard.writeText(cfg.callback!); } catch { /* ignore */ } toast({ tone: "success", title: t("settings.integrationDetail.copied", "Copied") }); }}>{t("settings.integrationDetail.copy", "Copy")}</Btn>
                  </div>
                </div>
              ) : null}
            </div>
          </Card>
          )}

          {/* §7 setup walkthrough status — server-checked, read-only */}
          {setup && Array.isArray(setup.steps) && setup.steps.length ? (
            <Card style={{ padding: 0 }}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.setupProgress", "Setup progress")}</h3>
                  <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.integrationDetail.checkedServerSideAgainstTheProvider", "Checked server-side against the provider.")}</p>
                </div>
                {setup.overall === "complete" ? <Pill tone="success" dot>{t("settings.integrationDetail.complete", "Complete")}</Pill> : setup.overall === "in_progress" ? <Pill tone="warn">{t("settings.integrationDetail.inProgress", "In progress")}</Pill> : <Pill tone="neutral">{t("settings.integrationDetail.incomplete", "Incomplete")}</Pill>}
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: "6px 0" }}>
                {setup.steps.map((s: SetupStatus["steps"][number]) => (
                  <li key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, flexShrink: 0, background: s.status === "complete" ? T.green1 : s.status === "failed" ? T.red1 : s.status === "in_progress" ? T.orange1 : T.n4 }} />
                    <span style={{ flex: 1, fontSize: 13, color: T.n1, textTransform: "capitalize" }}>{s.id.replace(/[-_]/g, " ")}</span>
                    <span style={{ fontSize: 11, color: s.status === "failed" ? T.red1 : T.soft, textTransform: "capitalize" }}>{s.status.replace(/_/g, " ")}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {hasOwnConfig && cfg.guestSync ? <SalesforceGuestSync cfg={cfg} /> : null}
          {hasOwnConfig && cfg.fieldMap ? <SalesforceFieldMap cfg={cfg} /> : null}

          {/* DNS records */}
          {cfg.dnsRecords ? (
            <Card style={{ padding: 0 }}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
                <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.dnsRecords", "DNS records")}</h3>
                <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.integrationDetail.addTheseToYourDomainRegistrarToAuthenticateTheSendingDomain", "Add these to your domain registrar to authenticate the sending domain.")}</p>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 12 }}>
                <thead>
                  <tr style={{ background: T.n6 }}>
                    <th style={thStyle}>{t("settings.integrationDetail.type", "Type")}</th><th style={thStyle}>{t("settings.integrationDetail.host", "Host")}</th><th style={thStyle}>{t("settings.integrationDetail.value", "Value")}</th><th style={thStyle}>{t("settings.integrationDetail.status", "Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {cfg.dnsRecords.map((r, i) => (
                    <tr key={i} style={{ borderTop: i ? `1px solid ${T.border}` : "none" }}>
                      <td style={{ ...tdStyle, fontFamily: T.fontMono, color: T.n1 }}>{r.type}</td>
                      <td style={{ ...tdStyle, fontFamily: T.fontMono, color: T.n1, fontSize: 11 }}>{r.host}</td>
                      <td style={{ ...tdStyle, fontFamily: T.fontMono, color: T.soft, fontSize: 11, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.value}</td>
                      <td style={tdStyle}>
                        {r.status === "verified" ? <Pill tone="success" dot>{t("settings.integrationDetail.verified", "Verified")}</Pill> :
                         r.status === "warn" ? <Pill tone="warn">{t("settings.integrationDetail.softFail", "Soft fail")}</Pill> :
                         <Pill tone="danger">{t("settings.integrationDetail.pending", "Pending")}</Pill>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <BrandLogo id={reg ? String(reg.provider) : id} name={pageName} brand={reg ? String(reg.brand || cfg.brand) : cfg.brand} glyph={reg ? String(reg.icon ?? "") : cfg.icon} size={44} radius={10} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{pageName}</div>
                <div style={{ fontSize: 12, color: T.soft }}>{pageAuth}</div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.5, margin: 0 }}>{pageDesc}</p>
          </Card>

          {isEthos ? (
            <>
              <EthosResourcesCard state={ethos} />
              <EthosClaimsCard state={ethos} />
            </>
          ) : reg ? (
            <RegistrationScopes detail={reg} />
          ) : (
          <Card style={{ padding: 20 }}>
            <div style={tx.label as CSSProperties}>{t("settings.integrationDetail.permissionsScopes", "Permissions / scopes")}</div>
            <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {cfg.permissions.map((p) => (
                <li key={p} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.n1 }}>
                  <span style={{ color: T.green1, display: "inline-flex" }}><Icon.check size={12} /></span>
                  <code style={{ fontFamily: T.fontMono, fontSize: 11 }}>{p}</code>
                </li>
              ))}
            </ul>
          </Card>
          )}

          {/* Pass front preview — renders itself only for a registration that
            * actually carries pass image uploads (returns null otherwise). Sits
            * above activity: it is what the operator checks while uploading. */}
          {reg ? <WalletPassPreview detail={reg} /> : null}

          {isEthos ? (
            <EthosVerifyHistoryCard state={ethos} />
          ) : reg ? (
            <RegistrationActivity detail={reg} />
          ) : (
          <Card style={{ padding: 0 }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <div style={tx.label as CSSProperties}>{t("settings.integrationDetail.recentActivity", "Recent activity")}</div>
            </div>
            {activityRows.length === 0 ? (
              <div style={{ padding: 20, fontSize: 12, color: T.soft, textAlign: "center" }}>
                {t("settings.integrationDetail.noActivityYetConnectToStartLoggingEvents", "No activity yet — connect to start logging events.")}</div>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {activityRows.map((a, i) => (
                  <li key={i} style={{ padding: "10px 16px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      {a.outcome ? <span style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, background: a.outcome === "failure" ? T.orange1 : T.green1 }} /> : null}
                      <span style={{ fontSize: 12, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.e}</span>
                    </span>
                    <span style={{ fontSize: 11, color: T.soft, flexShrink: 0 }}>{a.at}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          )}

          <Card style={{ padding: 20, background: T.blue2, borderColor: `color-mix(in srgb, ${T.blue1} 19%, transparent)` }}>
            <div style={{ display: "flex", gap: 10 }}>
              <span style={{ color: T.blue1, flexShrink: 0, marginTop: 2, display: "inline-flex" }}><Icon.info size={14} /></span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.blue1 }}>{t("settings.integrationDetail.needHelp", "Need help?")}</div>
                <div style={{ fontSize: 12, color: T.n1, marginTop: 4, lineHeight: 1.5 }}>
                  {t("settings.integrationDetail.readThe", "Read the")} <a href="#" onClick={(e) => e.preventDefault()} style={{ color: T.p1, fontWeight: 600 }}>{pageName} {t("settings.integrationDetail.setupGuide", "setup guide")}</a>{t("settings.integrationDetail.orContactYourCsmForWhiteGloveConfiguration", ", or contact your CSM for white-glove configuration.")}</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {/* Item 2 (addendum): shared-registration disconnect confirmation — names the
       * cascade (creds removed + every feature off + sync stops). Dynamic name +
       * feature list stay a literal template pending their ICU keys (REQ-067),
       * same precedent as the PayPal state copy above. */}
      {reg ? (
        <Modal open={confirmDisc} onClose={() => setConfirmDisc(false)} title={t("settings.integrationDetail.disconnectRegistrationTitle", "Disconnect this app registration?")} maxWidth={460}>
          <Modal.Body>
            <p style={{ margin: 0, fontSize: 13, color: T.n1, lineHeight: 1.6 }}>
              {t("settings.integrationDetail.disconnectConfirm", "Disconnect {page}? This removes the shared {page} credentials and turns off {features}. Calendar and booking sync stop until you reconnect.", { page: pageName, features: (reg.features ?? []).map((f) => f.name).filter(Boolean).join(", ") || t("settings.integrationDetail.everyFeature", "every feature") })}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Btn variant="ghost" disabled={busyAct != null} onClick={() => setConfirmDisc(false)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn danger disabled={busyAct != null} onClick={() => { setConfirmDisc(false); void doDisconnect(); }}>{busyAct === "disconnect" ? t("settings.integrationDetail.disconnecting", "Disconnecting…") : t("account.details.disconnect", "Disconnect")}</Btn>
          </Modal.Footer>
        </Modal>
      ) : null}
    </SettingsShell>
  );
}

/* ─── Salesforce: new-guest sync mode ────────────────────────────────── */
function SalesforceGuestSync({ cfg }: { cfg: IntegConfig }) {
  const t = useT();
  const gs = cfg.guestSync!;
  const [mode, setMode] = useState(gs.mode);
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.newGuestSync", "New guest sync")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.integrationDetail.whatHappensInSalesforceTheFirstTimeAPersonRegistersForOne", "What happens in Salesforce the first time a person registers for one of your events.")}</p>
        </div>
        <Pill tone="info" dot>{t("settings.integrationDetail.bookingTrigger", "Booking trigger")}</Pill>
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        {gs.options.map((opt) => {
          const active = mode === opt.id;
          const off = opt.id === "off";
          return (
            <label key={opt.id} onClick={() => setMode(opt.id)}
              style={{ display: "flex", gap: 12, padding: "12px 14px", border: `1px solid ${active ? "#00A1E0" : T.border}`, borderRadius: 6, background: active ? "rgba(0,161,224,0.03)" : T.white, cursor: "pointer", alignItems: "flex-start", transition: "border-color 120ms, background 120ms" }}>
              <span style={{ width: 16, height: 16, borderRadius: 999, flexShrink: 0, marginTop: 2, border: `2px solid ${active ? "#00A1E0" : T.n4}`, background: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {active ? <span style={{ width: 6, height: 6, borderRadius: 999, background: "#00A1E0" }} /> : null}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{opt.label}</span>
                  {opt.recommended ? <Pill tone="success">{t("settings.integrationDetail.recommended", "Recommended")}</Pill> : null}
                  {off ? <Pill tone="neutral">{t("settings.integrationDetail.noCrmWrite", "No CRM write")}</Pill> : (
                    <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.soft, background: T.n6, padding: "2px 6px", borderRadius: 3 }}>
                      {opt.id === "lead" ? t("settings.integrationDetail.text8", "sObject: Lead") : t("settings.integrationDetail.text9", "sObject: Contact")}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: T.soft, marginTop: 4, lineHeight: 1.5 }}>{opt.sub}</div>
              </div>
            </label>
          );
        })}
        {mode !== "off" ? (
          <div style={{ marginTop: 4, padding: 12, background: T.n6, borderRadius: 6, border: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: T.soft, marginTop: 2, flexShrink: 0, display: "inline-flex" }}><Icon.network size={12} /></span>
              <div style={{ fontSize: 12, color: T.n1 }}><strong>{t("settings.integrationDetail.deduplication", "Deduplication:")}</strong> <span style={{ color: T.n2 }}>{gs.dedupe}</span></div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: T.soft, marginTop: 2, flexShrink: 0, display: "inline-flex" }}><Icon.refresh size={12} /></span>
              <div style={{ fontSize: 12, color: T.n1 }}><strong>{t("settings.integrationDetail.onUpdate", "On update:")}</strong> <span style={{ color: T.n2 }}>{gs.onUpdate}</span></div>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/* ─── Salesforce: field mapping table ────────────────────────────────── */
function SalesforceFieldMap({ cfg }: { cfg: IntegConfig }) {
  const t = useT();
  const gsMode = cfg.guestSync?.mode || "lead";
  const [target, setTarget] = useState(gsMode === "contact" ? "contact" : "lead");
  const targetCol = target === "lead" ? "sfLead" : "sfContact";
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.integrationDetail.fieldMapping", "Field mapping")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.integrationDetail.eightBookingFieldsArePushedToTheMatchingSalesforceRecordOnCreate", "Eight booking fields are pushed to the matching Salesforce record on create and on every update.")}</p>
        </div>
        {/* The two Salesforce record targets are configuration, not data. */}
        <SegmentedControl<string>
          size="sm"
          ariaLabel={t("settings.integrationDetail.ariaLabel", "Mapping target")}
          value={target}
          onChange={setTarget}
          options={[
            { value: "lead", label: t("settings.integrationDetail.leadField", "Lead field") },
            { value: "contact", label: t("settings.integrationDetail.contactField", "Contact field") },
          ]}
        />
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 12 }}>
        <thead>
          <tr style={{ background: T.n6 }}>
            <th style={thStyle}>{t("settings.integrationDetail.eventsInStudio", "Events in Studio")}</th>
            <th style={thStyle}>{t("settings.integrationDetail.salesforce", "→ Salesforce")}{" "}{target === "lead" ? t("settings.integrationDetail.text6", "Lead") : t("settings.integrationDetail.text7", "Contact")}</th>
            <th style={thStyle}>{t("settings.integrationDetail.required", "Required")}</th>
            <th style={thStyle}>{t("settings.integrationDetail.notes", "Notes")}</th>
          </tr>
        </thead>
        <tbody>
          {cfg.fieldMap!.map((r, i) => (
            <tr key={i} style={{ borderTop: i ? `1px solid ${T.border}` : "none" }}>
              <td style={{ ...tdStyle, color: T.n1, fontWeight: 500 }}>{r.jrni}</td>
              <td style={{ ...tdStyle, fontFamily: T.fontMono, color: T.p1, fontSize: 11 }}>{(r as any)[targetCol]}</td>
              <td style={tdStyle}>
                {r.required ? <Pill tone="info">{t("settings.integrationDetail.required", "Required")}</Pill> : <span style={{ fontSize: 11, color: T.soft }}>{t("settings.integrationDetail.optional", "Optional")}</span>}
              </td>
              <td style={{ ...tdStyle, color: T.soft, fontSize: 11, lineHeight: 1.5 }}>{r.notes || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 12, color: T.soft }}>{cfg.fieldMap!.length} {t("settings.integrationDetail.fieldsMappedMatchingOnEmail", "fields mapped · matching on Email")}</span>
        <Btn variant="ghost" size="sm" icon={<Icon.edit size={12} />}>{t("settings.integrationDetail.editMappings", "Edit mappings")}</Btn>
      </div>
    </Card>
  );
}
